import styles from './OverviewPanel.module.css';
import {
    FormattedCurrency,
    intlFormatCurrency
} from '@iwIntlProvider/FormattedCurrency';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { FormattedMessage, useIntl } from 'react-intl';
import outstandingViewMessages from '../../OutstandingView/OutstandingView.messages';
import overviewPanelMessages from './OverviewPanel.messages';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { Pill } from '@iwoca/orion';

const OverviewPanel = ({ accountId }: { accountId: string }) => {
    const stateKey = useStateKey();
    const intl = useIntl();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;

    const outstandingBalance = account.balance!.total;
    const interest = account.balance!.interest;
    const principal = account.balance!.principal;

    const nextRepaymentAmount = intlFormatCurrency({
        intl,
        value: account.scheduled_payments!.schedule![0].total
    });
    const nextRepaymentDate = intl.formatDate(
        account.scheduled_payments!.schedule![0].date,
        {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }
    );

    const interestOnlyPeriodActive =
        account.is_in_interest_only_repayment_period;

    return (
        <div className={styles.overviewContainer}>
            <div>
                <p className={styles.loanInfoHeading}>
                    <FormattedMessage
                        {...outstandingViewMessages.outstandingBalance}
                    />
                </p>
                <div className={styles.outstandingBalance}>
                    <FormattedCurrency value={outstandingBalance!} />
                </div>
            </div>

            <div className={styles.loanBreakdownContainer}>
                <div className={styles.currentPrincipal}>
                    <div>
                        <p className={styles.loanInfoHeading}>
                            <FormattedMessage
                                {...outstandingViewMessages.currentPrincipal}
                            />
                        </p>
                    </div>
                    <FormattedCurrency
                        value={principal!}
                        className={styles.loanInfo}
                    />
                </div>

                <div>
                    <p className={styles.loanInfoHeading}>
                        <FormattedMessage
                            {...outstandingViewMessages.interestAccrued}
                        />
                    </p>
                    <FormattedCurrency
                        value={interest!}
                        className={styles.loanInfo}
                    />
                </div>

                <div>
                    <p className={styles.loanInfoHeading}>
                        <FormattedMessage
                            {...overviewPanelMessages.nextRepayment}
                        />
                        {interestOnlyPeriodActive && (
                            <Pill className="ml-s">
                                <FormattedMessage
                                    {...overviewPanelMessages.interestOnlyPillName}
                                />
                            </Pill>
                        )}
                    </p>
                    <span className={styles.loanInfo}>
                        <FormattedMessage
                            {...overviewPanelMessages.nextRepaymentDetail}
                            values={{ nextRepaymentAmount, nextRepaymentDate }}
                        />
                    </span>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default OverviewPanel;
